import React from "react";
import s from "./branding.module.css";
import { useTranslation } from "react-i18next";
import { Button } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { UTILS } from "utils";
import { useSelector } from "react-redux";

const MENU_ITEM_ROWS = 7;

const PreviewSectionCard = ({ title, children, className = "", onRefresh }) => {
  const { t } = useTranslation();
  return (
    <div
      className={`rounded-2xl border border-sky-100 bg-sky-50/90 p-5 shadow-[0_4px_24px_rgba(14,116,144,0.08)] ${className}`}
    >
      <div className="mb-5 flex flex-wrap items-center justify-between gap-2">
        <h2 className="m-0 text-base font-semibold capitalize tracking-tight text-slate-800">
          {title}
        </h2>
        {onRefresh ? (
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-lg border border-sky-300 bg-white px-3 py-1.5 text-sm font-medium text-sky-600 shadow-sm transition hover:bg-sky-50"
            onClick={onRefresh}
          >
            <ReloadOutlined className="text-sm" />
            {t("Refresh")}
          </button>
        ) : null}
      </div>
      {children}
    </div>
  );
};

const BrandingPreviewMenuWidget = ({
  settings,
  orderNowPopupConfig,
  onPreviewRefresh,
}) => {
  const { t } = useTranslation();
  const dir = UTILS.getLanguageInformation()?.direction;
  const { currentLanguage } = useSelector((state) => state.LanguageSlice);
  const closeBtn = dir === "rtl" ? "left-4" : "right-4";

  const accent = settings?.color || "#c2410c";
  const onAccent = settings?.textColor || "#ffffff";

  return (
    <div className={s.designright}>
      <div className="preview flex w-full min-w-0 flex-col gap-6">
        {/* Ordering menu preview */}
        <PreviewSectionCard
          title={t("Ordering menu preview")}
          onRefresh={onPreviewRefresh}
        >
          <div className="flex justify-center py-2">
            <div className={`${s.sotre_preview} mx-auto flex min-h-0 flex-col bg-white`}>
              {settings?.brandImageHomeBG ? (
                <div className="h-[100px] w-full shrink-0 overflow-hidden rounded-t-[15px] border-b border-gray-100">
                  <img
                    src={settings.brandImageHomeBG}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                </div>
              ) : null}
              {/* Fixed header */}
              <div className="border-b border-gray-100 px-3 pb-3 pt-3">
                <h4 className="mb-2 line-clamp-2 text-sm font-semibold leading-snug text-gray-900">
                  {settings?.taglineEN || t("Your tagline")}
                </h4>
                <div className="mb-2 flex items-center gap-2">
                  <div
                    className="h-1 w-1.5 rounded-full"
                    style={{ backgroundColor: accent }}
                  />
                  <div className="h-1 w-16 rounded bg-gray-200" />
                  <div
                    className="h-1 w-1.5 rounded-full"
                    style={{ backgroundColor: accent }}
                  />
                  <div className="h-1 w-12 rounded bg-gray-200" />
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <div
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-100"
                    style={{ color: accent }}
                  >
                    <svg
                      viewBox="0 0 512 512"
                      fill="currentColor"
                      className="h-4 w-4"
                      aria-hidden
                    >
                      <path d="M500.59 157.733L424.118 9.035C421.33 3.614 415.765 0 409.067 0H102.931c-.042 0-.084.003-.127.004-.039-.001-.079-.004-.117-.004h-.007a16.648 16.648 0 00-14.799 9.035L11.408 157.733c-2.703 5.258-2.351 11.53.594 16.287.169.336 230.213 330.71 230.443 331.031 6.688 9.336 20.637 9.252 27.192-.17 1.278-1.835 229.948-330.18 230.396-330.925a16.685 16.685 0 00.557-16.223zM381.782 33.299l-49.201 95.672c-28.87-56.027-17.64-34.209-49.248-95.672h98.449zM256.011 53.071c28.797 55.887 17.579 34.093 49.225 95.627h-98.501l49.276-95.627zm-27.295-19.772l-49.299 95.672-49.201-95.672h98.5zM102.8 53.217l49.2 95.481H53.539L102.8 53.217zm-44.706 128.78h108.102l54.01 232.854L58.094 181.997zM255.973 421.68l-55.594-239.682h111.188L255.973 421.68zm35.761-6.805l54.016-232.878H453.9L291.734 414.875zM360 148.698l49.298-95.672 49.202 95.672H360z" />
                    </svg>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 h-1 w-24 rounded bg-gray-300" />
                    <div className="h-1 w-14 rounded bg-gray-200" />
                  </div>
                </div>
              </div>

              <div className="flex items-start justify-between gap-2 border-b border-gray-100 px-3 py-2.5">
                <div>
                  <div className="mb-1 h-1 w-10 rounded bg-gray-300" />
                  <div className="flex items-center gap-1">
                    <div
                      className="h-1.5 w-1.5 rounded-full"
                      style={{ backgroundColor: accent }}
                    />
                    <div className="h-1 w-11 rounded bg-gray-200" />
                  </div>
                </div>
                <div
                  className="shrink-0 rounded-md px-2.5 py-1 text-[10px] font-medium"
                  style={{
                    backgroundColor: accent,
                    color: onAccent,
                  }}
                >
                  {t("Delivery")}
                </div>
              </div>

              {/* Categories */}
              <div className="scrollbar-thumb-only flex gap-1.5 overflow-x-auto border-b border-gray-100 px-3 py-2.5">
                {[0, 1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="shrink-0 rounded-lg px-2 py-1 text-[9px] font-medium"
                    style={
                      i === 0
                        ? {
                            backgroundColor: accent,
                            color: onAccent,
                          }
                        : {
                            border: "1px solid #e5e7eb",
                            color: "#9ca3af",
                            backgroundColor: "#fff",
                          }
                    }
                  >
                    {t("Category")}
                  </div>
                ))}
              </div>

              {/* Scrollable menu items */}
              <div className="scrollbar-thumb-only relative min-h-0 flex-1 overflow-y-auto px-3 py-2.5">
                <div className="mb-2 h-1 w-14 rounded bg-gray-300" />
                {Array.from({ length: MENU_ITEM_ROWS }).map((_, idx) => (
                  <div
                    key={idx}
                    className="mb-2.5 flex items-start justify-between gap-2 last:mb-0"
                  >
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <div className="h-1 w-24 rounded bg-gray-400" />
                      <div className="h-1 w-28 rounded bg-gray-200" />
                      <div className="h-1 w-28 rounded bg-gray-200" />
                      <div className="h-1 w-16 rounded bg-gray-300" />
                    </div>
                    <div className="h-11 w-11 shrink-0 rounded-md bg-gray-200" />
                  </div>
                ))}
              </div>

              <div className="mt-auto border-t border-gray-100 px-3 py-2.5">
                <div
                  className={`${s.basketstyle} w-full`}
                  style={{
                    backgroundColor: accent,
                    color: onAccent,
                  }}
                >
                  {t("Add to basket")}
                </div>
              </div>
            </div>
          </div>
        </PreviewSectionCard>

        {/* Ordering widget preview */}
        <PreviewSectionCard
          title={t("Ordering widget preview")}
          onRefresh={onPreviewRefresh}
        >
          <div className="flex justify-center">
            <div
              className={`${s.preview_widget} flex w-full max-w-[360px] flex-col overflow-hidden rounded-2xl border border-gray-100 sm:flex-row sm:items-stretch`}
            >
              <svg
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
                className={`absolute top-3 z-[1] h-4 w-4 text-gray-400 ${closeBtn}`}
                aria-hidden
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M7.414 6l4.293-4.293L10.293.293 6 4.586 1.707.293.293 1.707 4.586 6 .293 10.293l1.414 1.414L6 7.414l4.293 4.293 1.414-1.414L7.414 6z"
                  fill="currentColor"
                />
              </svg>
              <div className="relative z-0 flex min-w-0 flex-1 flex-col justify-center px-5 pb-6 pt-10 sm:pb-5 sm:pt-8">
                <h3 className="mb-1 text-2xl font-bold leading-tight text-gray-900">
                  {orderNowPopupConfig?.title
                    ? orderNowPopupConfig.title
                    : t("Hey there")}
                </h3>
                <p className="mb-4 text-[15px] leading-relaxed text-gray-600">
                  {orderNowPopupConfig?.message
                    ? orderNowPopupConfig.message
                    : t("Order online now!")}
                </p>
                <div className="mt-auto">
                  <Button
                    type="primary"
                    size="large"
                    className="h-11 min-w-[140px] rounded-lg border-0 px-6 text-[15px] font-semibold shadow-sm"
                    style={{
                      backgroundColor: accent,
                      color: onAccent,
                    }}
                    onClick={() => {}}
                  >
                    {orderNowPopupConfig?.buttonText
                      ? orderNowPopupConfig.buttonText
                      : t("Order now!")}
                  </Button>
                  <div className="mt-3 text-center sm:text-left">
                    <span className="text-[10px] text-gray-500">
                      {t("Powered by")}{" "}
                      <span className={`uppercase ${s.clrr}`}>
                        {t("company name", {
                          company:
                            currentLanguage === "EN"
                              ? process.env.REACT_APP_COMPANY_NAME_EN
                              : process.env.REACT_APP_COMPANY_NAME_AR,
                        })}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
              <div
                className={`${s.widget_illustration} hidden min-h-[140px] shrink-0 sm:block sm:w-[42%]`}
                aria-hidden
              />
            </div>
          </div>
        </PreviewSectionCard>
      </div>
    </div>
  );
};

export default BrandingPreviewMenuWidget;
